<script>
    import _ from 'lodash'
    import {CONST} from '../shared/constants'

    export default {
        name: 'VGridformFieldToolbar',
        props: {
            field: {
                type: Object,
                default: () => null
            },
            data: {
                type: Object,
                default: () => null
            },
            // [fieldlist, row, column, fieldlist + row, fieldlist.rows, rows]
            rowinfo: {
                type: Array,
                default: () => []
            },
            showType: {
                type: Boolean,
                default: false
            },
            devTools: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                isSmall: this.$vuetify.breakpoint.smAndDown
            }
        },

        computed: {
            actions() {
                const type = _.get(this.data, 'def.type', 'text')
                return [
                    {
                        name: 'export',
                        label: this.i18n('actions.export'),
                        icon: 'mdi-file-export-outline',
                        color: 'accent',
                        hidden: !this.devTools
                    },
                    {
                        name: 'edit',
                        label: this.i18n('actions.edit'),
                        icon: 'mdi-pencil',
                        hidden: CONST.NOEDIT_FIELDS.includes(type)
                    },
                    {
                        name: 'delete',
                        label: this.i18n('actions.delete'),
                        icon: 'mdi-trash-can-outline'
                    },
                    {
                        name: 'clone',
                        label: this.i18n('actions.clone'),
                        icon: 'mdi-content-copy'
                    }
                ]
            },
            isFirstRow() {
                return this.rowinfo[1] === 0
            },
            isLastRow() {
                return this.rowinfo[1] === this.rowinfo[4] - 1
            },
            selected() {
                return this.data.selected
            }
        },

        mounted() {
            this.$nextTick(() => {
                const sbar = this.$refs['systembar']
                if (sbar && sbar.$el) {
                    if (sbar.$el.clientWidth < 400) this.isSmall = true
                }
            })
        },

        methods: {
            emit(action) {
                this.$emit('action', action, this.field, this.data)
            },

            i18n(key) {
                return this.$vuetify.lang.t(`$vuetify.gfb.${key}`)
            }
        }
    }
</script>

<template>
    <VSystemBar ref="systembar" class="vgfb-toolbar">
        <!-- Small field dropdown menu -->
        <VMenu v-if="isSmall && !field.editMode" :close-on-content-click="false" right>
            <template v-slot:activator="{ on }">
                <VIcon small v-on="on">mdi-dots-vertical</VIcon>
            </template>
            <VList dense>
                <VListItem @click.left="emit('edit')">
                    <VListItemIcon>
                        <VIcon>mdi-settings-outline</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ i18n('actions.edit') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VListItem @click.left="emit('clone')">
                    <VListItemIcon>
                        <VIcon>mdi-content-copy</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ i18n('actions.clone') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VListItem v-if="devTools" @click.left="emit('export')">
                    <VListItemIcon>
                        <VIcon>mdi-file-export-outline</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ i18n('actions.export') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VDivider/>
                <VListItem>
                    <VRow>
                        <VCol cols="3">
                            <VIcon :disabled="isFirstRow" @click.left="emit('first')">mdi-chevron-double-up</VIcon>
                        </VCol>
                    </VRow>
                    <VRow>
                        <VCol cols="3">
                            <VIcon :disabled="isFirstRow" @click.left="emit('up')">mdi-chevron-up</VIcon>
                        </VCol>
                    </VRow>
                    <VRow>
                        <VCol cols="3">
                            <VIcon :disabled="isLastRow" @click.left="emit('down')">mdi-chevron-down</VIcon>
                        </VCol>
                    </VRow>
                    <VRow>
                        <VCol cols="3">
                            <VIcon :disabled="isLastRow" @click.left="emit('last')">mdi-chevron-double-down</VIcon>
                        </VCol>
                    </VRow>
                </VListItem>
                <VDivider/>
                <VListItem @click.left="emit('delete')">
                    <VListItemIcon>
                        <VIcon color="error">mdi-trash-can-outline</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ i18n('actions.delete') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
            </VList>
        </VMenu>

        <!-- Selection -->
        <div v-if="!field.editMode" class="vgf-draggable-ignore">
            <!-- TODO tooltip for checkbox does not work -->
            <VTooltip bottom>
                <template v-slot:activator="{ on }">
                    <VCheckbox :value="selected" class="mt-4 pl-2" dense :ripple="false" v-on="on" @change="emit('selection')"/>
                </template>
                <span>{{ i18n('blockSelect') }}</span>
            </VTooltip>
        </div>

        <!-- Gridform field type -->
        <div v-if="showType && !field.editMode" class="vgfb-toolbar__title">{{ data.gftype }}</div>

        <div v-if="!field.editMode && field.export">
            <VIcon color="primary" small :title="i18n('field.exportHint')">mdi-file-export-outline</VIcon>
        </div>
        <div v-if="!field.editMode && field.required">
            <VIcon color="primary" small :title="i18n('field.requiredHint')">mdi-star-circle</VIcon>
        </div>

        <div class="vgf__rl-handle"></div>

        <VBtn v-if="field.editMode" text @click.left="emit('cancel')">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
        <VBtn v-if="field.editMode" text @click.left="emit('save')">{{ $vuetify.lang.t('$vuetify.actions.apply') }}</VBtn>

        <div v-if="!isSmall && !field.editMode" class="vgf-draggable-ignore">
            <template v-for="action in actions">
                <VDivider v-if="action.divider" :key="action.name" class="mx-2" vertical/>
                <VTooltip v-else-if="!action.hidden" :key="action.name" bottom :disabled="!action.label">
                    <template v-slot:activator="{ on }">
                        <VBtn icon x-small class="mx-1" :disabled="action.disabled" v-on="on" @click.left="emit(action.name)">
                            <VIcon :color="action.color" small>{{ action.icon }}</VIcon>
                        </VBtn>
                    </template>
                    <span v-text="action.label"/>
                </VTooltip>
            </template>
        </div>
    </VSystemBar>
</template>

<style lang="scss" scoped>
    .vgfb-toolbar {
        width: 100%;
        padding-right: 0;
        padding-left: 0;
        overflow: hidden;
        border-radius: 4px 4px 0 0;
    }
    .vgfb-toolbar__title {
        max-width: 80%;
        padding: 0 4px;
        overflow: hidden;
        font-weight: 500;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .vgf-draggable-ignore {
        cursor: default;
    }
    .vgf__rl-handle {
        flex: 1 1 auto;
        height: 100%;
        margin: 0 16px;
        cursor: move !important;
        &:hover {
            $size: 24px;
            $contrast: 0.07;
            $checkercolor: rgba(#000, $contrast);
            $angle: 45deg;
            $tp: 25%;

            background-image: linear-gradient($angle, $checkercolor $tp, transparent $tp),
                linear-gradient(-$angle, $checkercolor $tp, transparent $tp),
                linear-gradient($angle, transparent 3 * $tp, $checkercolor 3 * $tp),
                linear-gradient(-$angle, transparent 3 * $tp, $checkercolor 3 * $tp);
            background-repeat: repeat;
            background-position: 0 0, 0 $size / 2, $size / 2 -1 * $size / 2, -1 * $size / 2 0;
            background-size: $size $size;
        }
    }
</style>
